<template>
  <div class="performance-goals-table" v-loading="loading">
    <div class="table-header">
      <span class="unit-tip">单位：万</span>
      <el-button icon="el-icon-arrow-left" circle size="mini" @click="year -= 1"></el-button>
      <span class="title">{{ title }}</span>
      <el-button icon="el-icon-arrow-right" circle size="mini" @click="year += 1"></el-button>
    </div>
    <div class="table-content">
      <ve-table
        max-height="100%"
        rowKeyFieldName="user_id"
        :columns="tableColumns"
        :table-data="tableData"
        :footer-data="footerTableData"
        :editOption="editOption"
        border-y
        scrollWidth="100%"
        :cell-selection-option="cellSelectionOption"
        :row-style-option="rowStyleOption"
        :fixed-header="true"
        :fixed-footer="true"
      />
    </div>
  </div>
</template>

<script>
import { Vue, Component, Prop, Watch } from 'vue-property-decorator';
import _ from 'lodash';
import { Message } from 'element-ui';
import moment from 'moment';
import { MONTH_NAMES } from '@/constants';

const MONTHS = MONTH_NAMES;

function formatNumber(val) {
  const num = Number(val);
  return num ? `${num.toLocaleString()}w` : val;
}

const StrongText = {
  functional: true,
  render(h, { props }) {
    return <strong style="color: red;">{props.text}</strong>;
  }
}

@Component
export default class PerformanceGoals extends Vue {
  @Prop({ type: Array, default: () => [] }) users;
  @Prop(Boolean) disabled;
  @Prop({ type: String, default: 'finish_target' }) mode; // finish_target, sales_target, shipping_target

  year = moment().year();
  tableData = [];
  loading = false;
  editingCells = {}; // 存储正在编辑的单元格集合 { 'userId-month': { userId, month, value, originalValue } }

  get tableColumns() {
    return [
    {
      key: 'screen_name',
      field: 'screen_name',
      title: '业务员/月份',
      align: 'center',
      width: 200,
      fixed: 'left',
      renderFooterCell({ row }, h) {
        return h(StrongText, {
          props: {
            text: row.screen_name,
          }
        });
      }
    },
    ...MONTHS.map((month, index) => ({
      key: `${index + 1}`,
      field: `${index + 1}`,
      title: month,
      align: 'center',
      edit: true,
      width: 130,
      renderBodyCell: ({ row }, h) => {
        const monthValue = index + 1;
        const cellKey = `${row.user_id}-${monthValue}`;
        const editingCell = this.editingCells[cellKey];

        // 只有当值存在且与原始值不相等时，才显示保存和取消按钮
        const shouldShowButtons = editingCell &&
          editingCell.value !== '' &&
          editingCell.value !== null &&
          editingCell.value !== undefined &&
          editingCell.value !== editingCell.originalValue;

        if (shouldShowButtons) {
          return h('div', { class: 'cell-with-save' }, [
            h('span', formatNumber(editingCell.value)),
            h('el-button', {
              props: {
                type: 'primary',
                size: 'mini',
                icon: 'el-icon-check',
                loading: row.saving
              },
              style: {
                marginLeft: '5px'
              },
              on: {
                click: () => this.saveCell(row, monthValue)
              }
            }),
            h('el-button', {
              props: {
                size: 'mini',
                icon: 'el-icon-close'
              },
              style: {
                marginLeft: '5px'
              },
              on: {
                click: () => this.cancelEdit(row.user_id, monthValue)
              }
            })
          ]);
        }

        return formatNumber(row[monthValue]);
      },
      renderFooterCell({ row }, h) {
        return h(StrongText, {
          props: {
            text: formatNumber(row[`${index + 1}`]),
          }
        });
      }
    })),
    {
      key: 'total',
      field: 'total',
      title: '总计',
      align: 'center',
      width: 80,
      fixed: 'right',
      renderBodyCell({ row }) {
        return formatNumber(_.sum(MONTHS.map((item, index) => Number(row[`${index + 1}`]) || 0)));
      },
      renderFooterCell({ row }, h) {
        return h(StrongText, {
          props: {
            text: formatNumber(row.total),
          }
        });
      }
    },
    ]
  }

  cellSelectionOption = {
    enable: true,
  }

  rowStyleOption = {
    stripe: false,
  }

  get editOption() {
    return {
      beforeStartCellEditing: ({ column }) => {
        if (this.disabled) {
          return false;
        }
        const month = Number(column.field);
        if (!month) {
          return false;
        }
        return true;
      },
      beforeCellValueChange: ({ changeValue, column, row }) => {
        const month = Number(column.field);
        const cellKey = `${row.user_id}-${month}`;

        // 在值改变之前，保存原始值（如果还没有保存的话）
        if (!this.editingCells[cellKey]) {
          this.$set(this.editingCells, cellKey, {
            userId: row.user_id,
            month,
            value: row[column.field], // 暂存当前值
            originalValue: row[column.field] // 保存原始值
          });
        }

        // 允许空值
        if (changeValue === '' || changeValue === null || changeValue === undefined) {
          const prevValue = row[column.field];
          return prevValue !== '';
        }

        const num = Number(changeValue);
        if (num < 0 || Number.isNaN(num)) {
          Message.error('请输入大于或等于0的数字');
          return false;
        }
        const prevValue = row[column.field];
        return num !== prevValue;
      },
      afterCellValueChange: ({ row, column, changeValue }) => {
        const month = Number(column.field);

        // 处理空值情况
        let value = 0;
        if (changeValue !== '' && changeValue !== null && changeValue !== undefined) {
          value = Number(changeValue);
        }

        // 更新编辑状态中的新值，但保持originalValue不变
        const cellKey = `${row.user_id}-${month}`;
        if (this.editingCells[cellKey]) {
          this.$set(this.editingCells[cellKey], 'value', value);

          // 如果值为空或等于原始值，清除编辑状态
          if ((value === '' || value === null || value === undefined || value === 0) ||
              value === this.editingCells[cellKey].originalValue) {
            this.$delete(this.editingCells, cellKey);
          }
        }
      }
    }
  }

  get footerTableData() {
    const monthTotalMap = {
      1: 0,
      2: 0,
      3: 0,
      4: 0,
      5: 0,
      6: 0,
      7: 0,
      8: 0,
      9: 0,
      10: 0,
      11: 0,
      12: 0,
    }
    this.tableData.forEach(row => {
      MONTHS.forEach((month, index) => {
        const key = `${index + 1}`;
        monthTotalMap[key] += Number(row[key]);
      });
    });

    const total = _.sum(Object.values(monthTotalMap));

    return [
      {

        user_id: -1,
        screen_name: '总计',
        ...monthTotalMap,
        total,
      }
    ]
  }

  get title() {
    const modeText = {
      finish_target: '完结金额',
      sales_target: '开单金额',
      shipping_target: '出货金额',
    };
    return `${this.year}年度${modeText[this.mode]}业绩目标`;
  }

  async fetchData() {
    const $year = moment(`${this.year}-01-01`);
    const params = {
      start_on_gteq: $year.startOf('year').format('YYYY-MM-DD'),
      start_on_lteq: $year.endOf('year').format('YYYY-MM-DD'),
      page: 0,
      goal_type_eq: 'monthly',
    }
    const data = await this.$request.get('/performance_goals', params);

    const groupedData = _.mapValues(
      _.groupBy(data, 'user_id'),
      group => _.mapValues(
        _.groupBy(group, v => Number(v.start_on.split('-')[1])),
        monthGroup => monthGroup[0] // 只处理第一条performatn_goal
      )
    );
    // {
    //   [user_id]: {
    //     [month]: PerformanceGoal
    //   }
    // }
    return groupedData;
  }

  @Watch('year')
  async init() {
    this.loading = true;
    try {
      const userData = await this.fetchData();
      this.tableData = this.users.map(user => {
        const row = {
          user_id: user.id,
          screen_name: user.screen_name,
          saving: false,
        }
        _.range(1, 13).forEach(month => {
          const data = _.get(userData, `${user.id}.${month}`);
          const value = Number(_.get(data, this.mode));
          Object.assign(row, {
            [month]: Number.isNaN(value) ? '' : value / 10000, // 用于单元格编辑
            [`data_${month}`]: data || null, // 保存performance_goal用于判断是新增还是更新
          });
        });
        return row;
      });
    } finally {
      this.loading = false;
    }
  }

  created() {
    this.init();
  }

  async saveCell(row, month) {
    const cellKey = `${row.user_id}-${month}`;
    const editingCell = this.editingCells[cellKey];
    if (!editingCell) return;

    const value = editingCell.value;
    this.$delete(this.editingCells, cellKey); // 清除编辑状态

    await this.handleSave(row, month, value);
  }

  cancelEdit(userId, month) {
    const cellKey = `${userId}-${month}`;
    const editingCell = this.editingCells[cellKey];
    if (!editingCell) return;

    // 恢复原始值
    const row = this.tableData.find(r => r.user_id === userId);
    if (row) {
      this.$set(row, month, editingCell.originalValue);
    }

    this.$delete(this.editingCells, cellKey);
  }

  async handleSave(row, month, value) {
    Object.assign(row, {
      [month]: value,
      saving: true,
    });
    try {
      const body = {
        start_on: moment(`${this.year}-${month}`).startOf('day').format('YYYY-MM-DD'),
        user_id: row.user_id,
        [this.mode]: value * 10000,
        goal_type: 'monthly'
      }
      let updateData = null;
      if (row[`data_${month}`]) {
        updateData = await this.$request.put(`/performance_goals/${row[`data_${month}`].id}`, body);
      } else {
        updateData = await this.$request.post('/performance_goals', body);
      }
      row[`data_${month}`] = updateData;
      Message.success('保存成功');
    } catch (e) {
      Message.error('保存失败，请重试');
      const prevValue = Number(_.get(row[`data_${month}`], this.mode));
      row[month] = prevValue ? prevValue / 10000 : '';
    } finally {
      row.saving = false;
    }
  }
}
</script>

<style lang="scss" scoped>
.performance-goals-table {
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.table-content {
  flex: 1;
  overflow: hidden;
}

.vue-table-root,
:deep(.ve-table) {
  height: 100%;
}

.table-header {
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: $primary;
  color: #fff;
  padding: 5px 10px;
  position: relative;

  .unit-tip {
    position: absolute;
    left: 10px;
    color: #ffd700;
    background: rgba(255, 255, 255, 0.1);
    padding: 2px 6px;
    border-radius: 3px;
    font-size: 12px;
    font-weight: bold;
  }

  .title {
    margin: 0 10px;
  }
}

:deep(.cell-with-save) {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
}
</style>
